import type { ReactNode } from 'react';

import { Box } from '../primitives/Box.js';
import type { BoxProps } from '../primitives/Box.js';
import { cx } from '../primitives/cx.js';

export type CardElevation = 'flat' | 'raised' | 'glow';

/**
 * Depth is theme-dependent by design: in dark, a card rises by getting
 * *lighter* (shadows are invisible on near-black); in light, it rises by
 * casting a shadow. Both are encoded in the semantic tokens, so this
 * component doesn't branch on theme at all.
 */
const elevationClass: Record<CardElevation, string> = {
  flat: 'bg-surface border border-line',
  raised: 'bg-raised border border-line shadow-md',
  glow: 'bg-raised border border-transparent shadow-glow',
};

export type CardProps = BoxProps & {
  elevation?: CardElevation;
  padding?: 0 | 3 | 4 | 5 | 6;
  children?: ReactNode;
};

const padClass = { 0: '', 3: 'p-3', 4: 'p-4', 5: 'p-5', 6: 'p-6' } as const;

export function Card({
  elevation = 'flat',
  padding = 4,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <Box
      className={cx('rounded-xl', elevationClass[elevation], padClass[padding], className)}
      {...rest}
    >
      {children}
    </Box>
  );
}
